import {
  OKTA_LOGIN_URL, OKTA_LOGOUT_URL, LOCAL_STORAGE_ITEMS, SESSION_STORAGE_ITEMS, ENABLE_PENDO,
  NOTIFS_POLL_DELAY_TIME, PENDO_APPLICATION_NAME, SIGN_IN_AS_GUID, AUTH_ACCESS_TOKEN_API,
  AUTH_OAUTH2_LOGIN_API,
  EVONE_ACCESS_INGREDIENT,
  EVONE_APP_CODE,
  EVONE_CLIENT_ID,
} from 'constants/AppConstants';
import { get, isEmpty, random } from 'lodash';
import { jwtDecode } from 'jwt-decode';
/**
 * parse jsonData to return list of Micro Applications
 * @param {Object} jsonData - Micro Application json data from entitlement service
 * @returns {Object[]} mfaList - list of Micro Applications
 */
export const getMFAConfigFromJson = (jsonData) => {
  if (isEmpty(jsonData)) {
    return [];
  }
  const mfaConfig = jsonData.map((dataObj) => ({
    name: get(dataObj, 'name', ''),
    appId: get(dataObj, 'appCode', ''),
    rootUrl: get(dataObj, 'appUrl', ''),
    prefetch: get(dataObj, 'prefetchEnabled', false),
    order: get(dataObj, 'order', ''),
    tooltip: get(dataObj, 'tooltip', ''),
    icon: get(dataObj, 'icon', ''),
    // #genai preserved for EVONE entitlement checks (same shape as user entitled apps)
    entitlements: get(dataObj, 'entitlements', {}),
  }));
  return mfaConfig.sort((mfa1, mfa2) => mfa1.order - mfa2.order);
};

/**
 * Whether Evone banner should show: LD flag plus EVONE on third-party entitled-apps API with EVONE_ACCESS ingredient. #genai
 * Viewpoint MFA entitlements do not include third-party apps; use list from THIRD_PARTY_ENTITLEMENT_SERVICE_API.
 * @param {Object[]} thirdPartyAppList - apps from fetchUserEntitledApps / mapThirdPartyAppsForUi (appCode, entitlements)
 * @param {boolean} featureFlagEnabled - LaunchDarkly showHorizonBanner
 */
export const shouldShowEvoneBanner = (thirdPartyAppList, featureFlagEnabled) => {
  if (!featureFlagEnabled || !Array.isArray(thirdPartyAppList) || thirdPartyAppList.length === 0) {
    return false;
  }
  const evoneApp = thirdPartyAppList.find((app) => app.appCode === EVONE_APP_CODE);
  if (!evoneApp) {
    return false;
  }
  const ingredients = get(evoneApp, ['entitlements', EVONE_APP_CODE, 'ingredients'], []);
  return ingredients.includes(EVONE_ACCESS_INGREDIENT);
};

/**
 * Builds the auth-service OAuth2 login URL used to hand the user off to EVONE (Horizon). #genai
 * Replaces the previous {appURL}/login?user_name=… redirect: instead we hit
 * {REACT_APP_AUTH_SERVICE_URL}/v1/oauth2/login?redirect_uri={evoneAppLink}&client_id={EVONE_CLIENT_ID}&login_hint={userName}
 * so auth-service can perform the SSO and redirect the user back to EVONE.
 * Shared by both the start-page auto-redirect (UserEntitledApps) and the Horizon banner click (PrimaryNavBar).
 * @param {Object[]} thirdPartyAppList - mapped third-party apps (must contain an EVONE entry with appURL)
 * @param {string} [loginHint=''] - the logged-in user's username (email) to pass as login_hint
 * @returns {string} absolute URL, or '' if EVONE app / client id is missing
 */
export const buildEvoneOauthLoginUrl = (thirdPartyAppList, loginHint = '') => {
  if (!Array.isArray(thirdPartyAppList) || !EVONE_CLIENT_ID) {
    return '';
  }
  const evoneApp = thirdPartyAppList.find((app) => app.appCode === EVONE_APP_CODE);
  const raw = evoneApp?.appURL || evoneApp?.appUrl;
  if (!raw) {
    return '';
  }
  const params = new URLSearchParams({
    redirect_uri: raw,
    client_id: EVONE_CLIENT_ID,
  });
  if (loginHint) {
    params.set('login_hint', loginHint);
  }
  return `${AUTH_OAUTH2_LOGIN_API}?${params.toString()}`;
};

/**
 * generates and returns url to be used as okta redirect url
 * @returns {String} url
 */
export const getRedirectUrl = () => {
  const { location: { origin } } = window;
  return `${origin}/authorize/callback`;
};

/**
 * generates and returns okta login url
 * @param {String} state      - state value passed to okta
 * @param {String} [loginHint] - optional email to pre-fill the Okta sign-in form
 * @returns {String} oktaLoginUrl
 */
export const getOktaLoginUrl = (state, loginHint = '') => {
  const redirectUri = getRedirectUrl();
  const base = `${OKTA_LOGIN_URL}&redirect_uri=${redirectUri}&state=${state}`;
  return loginHint ? `${base}&login_hint=${encodeURIComponent(loginHint)}` : base;
};

/**
 * generates and redirects user to okta login
 * @param {String} [subRoute='/'] - URL to redirect to after successful login
 * @param {String} [loginHint=''] - optional email to pre-fill the Okta sign-in form
 */
export const redirectToOkta = (subRoute = '/', loginHint = '') => {
  const state = `state-${subRoute}`;
  const stateHash = window.btoa(state);
  sessionStorage.setItem(stateHash, subRoute);
  const oktaUrl = getOktaLoginUrl(stateHash, loginHint);
  window.location.assign(oktaUrl);
};

/**
 * Calculates expiry time of access token
 * @param {number} expiresIn Number of seconds in which the token expires
 * @returns {string} expiry time of the token
 */
export const calculateExpiryTime = (expiresIn) => {
  const currTime = new Date();
  const expiryTime = new Date(currTime.setSeconds(currTime.getSeconds() + expiresIn)).toString();
  return expiryTime;
};

/**
 * sets the auth token in local storage
 * @param {String} token
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem('token', JSON.stringify(token));
  localStorage.setItem('token', JSON.stringify(token));
};

/**
 * Generates a random second between 0 and upper limit calculated based on timeoutWindow.
 */
export const getRandomSeconds = (timeoutWindow) => {
  const upperLimit = (timeoutWindow * 60) / 2;
  return Math.floor(random(0, 1, true) * Math.floor(upperLimit));
};

/**
 * gets the auth token stored in local storage
 * @returns {String} token
 */
export const getAuthToken = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  const authToken = (token && token.access_token) || '';
  return authToken;
};

/**
 * gets the expiry time of access token stored in local storage
 * @returns {String} expiry time of access token
 */
export const getTokenExpiryTime = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  const expiryTime = token && token.expiry_time;
  return expiryTime;
};

/**
 * gets the expires_in of access token stored in local storage
 * @returns {String} expiresIn
 */
export const getTokenExpiresIn = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  const expiresIn = token && token.expires_in;
  return expiresIn;
};

/**
 * Calculates the minutes representing the window before token expiry in which new token is fetched
 * @returns {number} minutes representing timeout window
 */
export const getTimeoutWindowMinutes = () => (getTokenExpiresIn() > 1800 ? 5 : 2);

/**
 * Generates a timeout time to be used by setTimeout
 * @param {Date} expiryTime expiry time of the access token
 * @returns {number} Seconds in which the setTimeout times out
 */
export const getTimeoutTime = (expiryTime) => {
  const currTime = new Date();
  const timeoutWindow = getTimeoutWindowMinutes();
  const randSec = getRandomSeconds(timeoutWindow);
  const timeoutTime = (expiryTime.getTime() - ((timeoutWindow * 60 - randSec) * 1000)) - currTime;
  return timeoutTime;
};

/**
 * checks if auth token is stored in local storage
 * @returns {Boolean} isAuthorized
 */
export const isAuthorized = () => !!localStorage.getItem('token');

/**
 * Returns true only when a real, non-expired user token exists in localStorage.
 *
 * Differences from isAuthorized():
 *   - Checks token.expiry_time written by setAuthToken() in Authorize.saga
 *   - Returns false for expired tokens instead of trusting mere existence
 *   - Returns false when access_token is missing (e.g. partially written objects)
 *
 * Use this wherever you need to decide "is the user actually still logged in?"
 * rather than just "did a token ever exist?".
 * #genai
 * @returns {boolean}
 */
export const isTokenValid = () => {
  try {
    const token = JSON.parse(localStorage.getItem('token'));
    if (!token || !token.access_token) return false;
    const { expiry_time: expiryTime } = token;
    if (!expiryTime) return false;
    return new Date().getTime() < new Date(expiryTime).getTime();
  } catch (_) {
    return false;
  }
};

/**
 * Clears an expired or invalid user token from localStorage/sessionStorage.
 * Does NOT touch ev_pre_access_token — the guest flow depends on that key
 * being set by SharedStartupGate before /signin is loaded.
 * #genai
 */
export const clearExpiredToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem('token'));
    // Only clear when the token is a real user token (has expiry written by Authorize.saga).
    // Skip if access_token or expiry_time is missing (e.g. pre-access tokens have no expiry).
    const expiryTime = token?.expiry_time;
    if (!token?.access_token || !expiryTime) return;
    if (new Date().getTime() >= new Date(expiryTime).getTime()) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    }
  } catch (_) {
    // noop — storage may be unavailable
  }
};

/**
 * Redirects an EV user to sign in, consistent with the logOut() pattern used
 * throughout the rest of the app (fetch interceptor 401, Authorize.saga errors, etc.).
 *
 * Why this instead of calling redirectToOkta() directly in the component:
 *   - Mirrors the logOut() → redirectToOkta() chain used app-wide
 *   - Supports an optional loginHint so Okta can pre-fill the user's email
 *   - Clears any stale/expired token before the redirect to prevent the
 *     next page load from incorrectly treating the user as already authenticated
 *
 * @param {string} [subRoute='/'] - Path to return to after successful sign-in
 * @param {string} [loginHint=''] - Email to pre-fill on the Okta sign-in form
 * #genai
 */
export const redirectEvUserToSignIn = (subRoute = '/', loginHint = '') => {
  clearExpiredToken();
  redirectToOkta(subRoute, loginHint);
};

/**
 * gets the id_token stored in local storage
 * @returns {String} id_token
 */
export const getAuthIdToken = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  const authIdToken = (token && token.id_token) || '';
  return authIdToken;
};

/**
 * clears certain items from session and local storage and logs user out
 */
export const signOutUser = () => {
  const idToken = getAuthIdToken();
  const redirectUri = window.location.origin;
  const urlsearch = new URLSearchParams();
  urlsearch.set('id_token_hint', idToken);
  urlsearch.set('post_logout_redirect_uri', redirectUri);
  if (window.location.pathname === '/start') {
    urlsearch.delete('post_logout_redirect_uri');
  }
  const logoutUrl = `${OKTA_LOGOUT_URL}?${urlsearch.toString()}`;
  SESSION_STORAGE_ITEMS.forEach((item) => sessionStorage.removeItem(item));
  LOCAL_STORAGE_ITEMS.forEach((item) => localStorage.removeItem(item));
  window.location.assign(logoutUrl);
};

/**
 * Signs out user with onboarding details stored for later redirect
 * @param {Object} onboardingDetails - Details needed for the final redirect
 */
export const signOutUserWithOnboardingDetails = (onboardingDetails = {}) => {
  const idToken = getAuthIdToken();

  // Store onboarding details in localStorage before logout
  localStorage.setItem('ev_onboarding_details', JSON.stringify(onboardingDetails));

  // Set redirect URI to our logout handler
  const redirectUri = `${window.location.origin}/logout-handler`;

  const urlsearch = new URLSearchParams();
  urlsearch.set('id_token_hint', idToken);
  urlsearch.set('post_logout_redirect_uri', redirectUri);

  const logoutUrl = `${OKTA_LOGOUT_URL}?${urlsearch.toString()}`;

  // Clear session and local storage (except our onboarding details)
  SESSION_STORAGE_ITEMS.forEach((item) => sessionStorage.removeItem(item));
  LOCAL_STORAGE_ITEMS.forEach((item) => {
    if (item !== 'ev_onboarding_details') {
      localStorage.removeItem(item);
    }
  });

  window.location.assign(logoutUrl);
};

/**
 * sets the monogram color in local storage
 * @param {String} monogramColour
 */
export const setMonogramColor = (monogramColour) => {
  if (!localStorage.getItem('ev_mgc')) {
    localStorage.setItem('ev_mgc', window.btoa(monogramColour));
  }
};

/**
 * check if the string is url or not.
 * @param {String} string
 */
export const isUrl = (string) => {
  try {
    return Boolean(new URL(string));
  } catch (e) { return false; }
};

/**
 * return sub-route using the hash.
 * @param {String} stateHash
 */
export const getSubRoute = (stateHash) => {
  const state = window.atob(stateHash);
  const storedRoute = sessionStorage.getItem(stateHash);
  sessionStorage.removeItem(stateHash);
  const subRoute = state.substring(state.indexOf('-') + 1);
  if (!isUrl(subRoute) && storedRoute === subRoute) {
    return subRoute;
  }
  return '/';
};

/**
 * Initialize pendo with visitor metadata
 * @param {string} appId
 */
export const initializePendo = (user) => {
  const { pendo } = window;
  const userName = user && user.userName ? user.userName : '';
  const visitor = {
    id: userName,
    email: userName,
    full_name: `${user.firstName} ${user.lastName}`,
    application: PENDO_APPLICATION_NAME,
  };

  const account = {
    id: user && user.organization ? user.organization.id : '',
    name: user && user.organization ? user.organization.name : '',
    application: PENDO_APPLICATION_NAME,
  };
  if (pendo) {
    if (pendo.visitorId !== userName) {
      pendo.initialize({
        visitor, account,
      });
    } else {
      pendo.updateOptions({
        visitor, account,
      });
    }
    // eslint-disable-next-line no-console
    console.log(pendo.validateInstall());
  }
};

/**
 * Return Rollbar access token from the env variable based on the appid
 * @param {*} appName
 */
export const getRollbarAccessToken = (appName) => {
  const appid = appName.toString().toUpperCase();
  const envVar = `REACT_APP_ROLLBAR_${appid}_CLIENT_TOKEN`;
  if (typeof process.env[envVar] !== 'undefined') {
    return process.env[envVar];
  }
  return process.env.REACT_APP_ROLLBAR_FALLBACK_CLIENT_TOKEN;
};

/**
 * Initialize rollbar
 * @param {string} appName
 */
export const initializeRollbar = (appName) => {
  const accessToken = getRollbarAccessToken(appName);
  if (window.Rollbar) {
    // eslint-disable-next-line no-param-reassign
    window.Rollbar.configure({ accessToken });
  }
};

/**
 * sets user details to be used by the micro-frontend app
 * @param {Object} userDetails
 */
// Helper function to safely encode strings with Unicode characters to base64
// Falls back to regular btoa for backwards compatibility when possible
const safeBtoa = (str) => {
  try {
    // Try regular btoa first for backwards compatibility
    return btoa(str);
  } catch (e) {
    // If it fails due to Unicode characters, use the safe encoding
    return btoa(unescape(encodeURIComponent(str)));
  }
};

export const setUserDetails = (userDetails) => {
  const encodedUserDetails = safeBtoa(JSON.stringify(userDetails));
  sessionStorage.setItem('ev_ud', encodedUserDetails);
  localStorage.setItem('ev_ud', encodedUserDetails);
  if (ENABLE_PENDO) {
    try {
      initializePendo(userDetails);
    } catch (error) {
      console.log('Pendo initialization failed:', error);
    }
  }
};

/**
 * gets delaytime for polling notifications
 */
export const getPollingDelayTime = () => {
  if (Number.isNaN(parseInt(NOTIFS_POLL_DELAY_TIME, 10))) {
    return 300;
  }
  return parseInt(NOTIFS_POLL_DELAY_TIME, 10);
};

export const parseJwt = (token) => {
  try {
    return JSON.parse(window.atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

/**
 * set token in local storage
 */
export const setLocalStorage = () => {
  if (localStorage.getItem('token') && !sessionStorage.getItem('token')) {
    sessionStorage.setItem('token', localStorage.getItem('token'));
  }
};

export const wait = (n) => new Promise((next) => {
  setTimeout(() => next(), n);
});

/** True when the JWT `iss` claim indicates an Okta-issued token. */
export const isOktaToken = (jwt) => {
  if (!jwt) return false;
  try {
    const { iss } = jwtDecode(jwt);
    return typeof iss === 'string'
      && (iss.includes('.okta.com') || iss.includes('.oktapreview.com'));
  } catch (_) {
    return false;
  }
};

/** True when the JWT has tokenType === "SHARE" (guest/shared-resource token). #genai */
export const isShareToken = (jwt) => {
  if (!jwt) return false;
  try {
    const decoded = jwtDecode(jwt);
    return String(decoded?.tokenType || '').toUpperCase() === 'SHARE';
  } catch (_) {
    return false;
  }
};

/** Extracts tokenGUID from a SHARE JWT for use as the shareGuid refresh header. #genai */
export const getShareGuid = (jwt) => {
  if (!jwt) return '';
  try {
    const decoded = jwtDecode(jwt);
    return decoded?.tokenGUID || '';
  } catch (_) {
    return '';
  }
};

// True when the user is in a sign-in-as (impersonation) session; skip guest/shared-resource APIs.
export const isSignInAsToken = () => Boolean(localStorage.getItem(SIGN_IN_AS_GUID));

export const shouldSkip401Handling = (url, headersMap) => {
  const headers = headersMap.get(url);
  if (headers && headers.has('skip401Handling') && headers.get('skip401Handling')) {
    return true;
  }
  if (url === AUTH_ACCESS_TOKEN_API) {
    if (headers && headers.has(SIGN_IN_AS_GUID) && headers.get(SIGN_IN_AS_GUID)) {
      return true;
    }
  }
  // Skip 401 handling for logout-handler path
  if (window.location.pathname === '/logout-handler') {
    return true;
  }
  return false;
};

/** Decodes the user's email (or sub) from the access token in localStorage. */
export const getUserEmailFromToken = () => {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) return '';
    const { access_token: jwt } = JSON.parse(raw);
    if (!jwt) return '';
    const decoded = jwtDecode(jwt);
    return (decoded && (decoded.email || decoded.sub)) || '';
  } catch (_) {
    return '';
  }
};

/**
 * Signs out a guest user (non-Okta, custom auth token).
 * Clears all tokens from storage then reloads the current resource URL so
 * SharedStartupGate detects no session and restarts the guest sign-in flow.
 * Does NOT call the Okta logout endpoint — guest users have no Okta session. #genai
 */
export const signOutGuest = () => {
  SESSION_STORAGE_ITEMS.forEach((item) => sessionStorage.removeItem(item));
  LOCAL_STORAGE_ITEMS.forEach((item) => localStorage.removeItem(item));
  window.location.assign(window.location.pathname + window.location.search);
};

/**
 * Signs out a guest user and redirects to /signin?redirect={currentResource}.
 * Used by sidebar guest logout so the user returns to sign-in with the current resource. #genai
 * @param {string} currentResource - path + search to pass as redirect (e.g. window.location.pathname + window.location.search)
 */
export const signOutGuestToSignIn = (currentResource) => {
  SESSION_STORAGE_ITEMS.forEach((item) => sessionStorage.removeItem(item));
  LOCAL_STORAGE_ITEMS.forEach((item) => localStorage.removeItem(item));
  const redirect = encodeURIComponent(currentResource || window.location.pathname + window.location.search);
  window.location.assign(`${window.location.origin}/signin?redirect=${redirect}`);
};

/** Basic email format validation. */
export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
